import React, { useState, useEffect, useMemo } from 'react';
import MainSidebar from '../../components/ui/MainSidebar';
import { NavigationHeader } from '../../components/ui/NavigationHeader';
import QuoteDataGrid from './components/QuoteDataGrid';
import FilterSidebar from './components/FilterSidebar';
import QuickStatsCards from './components/QuickStatsCards';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { Quote, FilterCriteria } from './types';
import { quoteService } from '../../services/quoteService';

const QuotesOverviewDashboard: React.FC = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedQuotes, setSelectedQuotes] = useState<string[]>([]);
  const [sortColumn, setSortColumn] = useState('lastModified');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterVisible, setFilterVisible] = useState(true);

  const userRole: 'manager' | 'sales-rep' = 'manager';

  const [filters, setFilters] = useState<FilterCriteria>({
    statuses: ['DRAFT', 'OPEN', 'CANCELLED', 'APPROVED'],
    dateRange: { start: null, end: null },
    customerSearch: '',
    referenceSearch: '',
    valueRange: { min: 0, max: 1000000 },
  });

  // Fetch quotes from Supabase
  useEffect(() => {
    let isMounted = true;

    const loadQuotes = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await quoteService.getAll();
        
        if (isMounted) {
          // Convert database quotes to UI format
          const uiQuotes: Quote[] = data.map((dbQuote: any) => ({
            id: dbQuote.id,
            referenceCode: dbQuote.reference_code,
            customerName: dbQuote.customer_name,
            companyName: dbQuote.company_name, // Use fetched company name
            projectTitle: dbQuote.project_name,
            status: dbQuote.status,
            createdDate: new Date(dbQuote.quote_date),
            lastModified: new Date(dbQuote.updated_at || dbQuote.created_at || dbQuote.quote_date),
            totalValue: Number(dbQuote.total_ex_vat),
            assignedSalesRep: dbQuote.latest_user || 'Unknown User', // Use fetched user name
            salesRepEmail: 'sales@evoqia.eu',
            latestUser: dbQuote.latest_user, // Store latest user
            profitMargin: 30,
            crmSyncStatus: 'synced' as const,
            emailDeliveryStatus: 'delivered' as const,
          }));
          setQuotes(uiQuotes);
        }
      } catch (err) {
        if (isMounted) {
          console.error('Error loading quotes:', err);
          setError(err instanceof Error ? err.message : 'Failed to load quotes');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadQuotes();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleQuotesChange = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await quoteService.getAll();
      
      // Convert database quotes to UI format
      const uiQuotes: Quote[] = data.map((dbQuote: any) => ({
        id: dbQuote.id,
        referenceCode: dbQuote.reference_code,
        customerName: dbQuote.customer_name,
        companyName: dbQuote.company_name, // Use fetched company name
        projectTitle: dbQuote.project_name,
        status: dbQuote.status,
        createdDate: new Date(dbQuote.quote_date),
        lastModified: new Date(dbQuote.updated_at || dbQuote.created_at || dbQuote.quote_date),
        totalValue: Number(dbQuote.total_ex_vat),
        assignedSalesRep: dbQuote.latest_user || 'Unknown User', // Use fetched user name
        salesRepEmail: 'sales@evoqia.eu',
        latestUser: dbQuote.latest_user, // Store latest user
        profitMargin: 30,
        crmSyncStatus: 'synced' as const,
        emailDeliveryStatus: 'delivered' as const,
      }));
      setQuotes(uiQuotes);
    } catch (err) {
      console.error('Error reloading quotes:', err);
      setError(err instanceof Error ? err.message : 'Failed to reload quotes');
    } finally {
      setLoading(false);
    }
  };

  const filteredQuotes = useMemo(() => {
    let result = quotes;

    if (filters.statuses.length > 0) {
      result = result.filter((q) => filters.statuses.includes(q.status));
    }

    if (filters.dateRange.start) {
      result = result.filter((q) => q.createdDate >= filters.dateRange.start!);
    }

    if (filters.dateRange.end) {
      result = result.filter((q) => q.createdDate <= filters.dateRange.end!);
    }

    if (filters.customerSearch) {
      const searchLower = filters.customerSearch.toLowerCase();
      result = result.filter((q) => {
        const customerNameMatch = q.customerName.toLowerCase().includes(searchLower);
        const companyNameMatch = q.companyName?.toLowerCase().includes(searchLower);
        return customerNameMatch || companyNameMatch;
      });
    }

    if (filters.referenceSearch) {
      const searchLower = filters.referenceSearch.toLowerCase();
      result = result.filter((q) => 
        q.referenceCode.toLowerCase().includes(searchLower)
      );
    }

    result = result.filter(
      (q) => q.totalValue >= filters.valueRange.min && q.totalValue <= filters.valueRange.max
    );

    return result;
  }, [quotes, filters]);

  const sortedQuotes = useMemo(() => {
    const sorted = [...filteredQuotes];
    sorted.sort((a, b) => {
      let aValue: any = a[sortColumn as keyof Quote];
      let bValue: any = b[sortColumn as keyof Quote];

      if (aValue instanceof Date) aValue = aValue.getTime();
      if (bValue instanceof Date) bValue = bValue.getTime();

      if (typeof aValue === 'string') {
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    });
    return sorted;
  }, [filteredQuotes, sortColumn, sortDirection]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleSelectQuote = (quoteId: string) => {
    setSelectedQuotes((prev) =>
      prev.includes(quoteId) ? prev.filter((id) => id !== quoteId) : [...prev, quoteId]
    );
  };

  const handleSelectAll = (selected: boolean) => {
    setSelectedQuotes(selected ? sortedQuotes.map((q) => q.id) : []);
  };

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'j' || e.key === 'k') {
        e.preventDefault();
      }
      if (e.key === 'd' && selectedQuotes.length === 1) {
        e.preventDefault();
        console.log('Duplicate quote:', selectedQuotes[0]);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [selectedQuotes]);

  if (loading) {
    return (
      <div className="flex h-screen bg-background">
        <MainSidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
        <div className={`flex-1 flex items-center justify-center transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-60'}`}>
          <div className="text-center">
            <Icon name="Loader" size={48} className="text-primary animate-spin mx-auto mb-4" />
            <p className="text-text-secondary">Loading quotes...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen bg-background">
        <MainSidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
        <div className={`flex-1 flex items-center justify-center transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-60'}`}>
          <div className="text-center max-w-md">
            <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" />
            <p className="text-lg font-medium text-text-primary mb-2">Error Loading Quotes</p>
            <p className="text-sm text-text-secondary mb-6">{error}</p>
            <Button onClick={() => window.location.reload()}>Retry</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-background">
      <MainSidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />

      <div className={`flex-1 flex flex-col transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-60'}`}>
        <NavigationHeader />

        <main className="flex-1 overflow-auto">
          <div className="max-w-[1920px] mx-auto p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-semibold text-text-primary mb-2">Quotes Overview</h1>
                <p className="text-text-secondary">
                  Manage and monitor your quote pipeline
                </p>
              </div>
              <div className="text-center">
                <h2 className="text-2xl font-bold text-text-primary">EVOQIA QUOTING APP</h2>
                <p className="text-sm text-text-secondary">Version 1.3</p>
              </div>
            </div>

            <QuickStatsCards quotes={quotes} />

            <div className="flex items-center gap-4">
              <Button
                variant={filterVisible ? 'default' : 'outline'}
                size="sm"
                iconName={filterVisible ? 'FilterX' : 'Filter'}
                onClick={() => setFilterVisible(!filterVisible)}
              >
                {filterVisible ? 'Hide Filters' : 'Show Filters'}
              </Button>
            </div>

            <FilterSidebar
              filters={filters}
              onFilterChange={setFilters}
              isVisible={filterVisible}
            />

            <QuoteDataGrid
              quotes={sortedQuotes}
              selectedQuotes={selectedQuotes}
              onSelectQuote={handleSelectQuote}
              onSelectAll={handleSelectAll}
              userRole={userRole}
              onSort={handleSort}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onQuotesChange={handleQuotesChange}
            />

            {sortedQuotes.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 bg-card rounded-lg border border-border">
                <Icon name="Search" size={48} className="text-text-secondary mb-4" />
                <p className="text-lg font-medium text-text-primary mb-2">No quotes found</p>
                <p className="text-sm text-text-secondary mb-6">
                  Try adjusting your filters or search criteria
                </p>
                <Button
                  variant="outline"
                  onClick={() => {
                    setFilters({
                      statuses: ['DRAFT', 'OPEN', 'CANCELLED', 'APPROVED'],
                      dateRange: { start: null, end: null },
                      customerSearch: '',
                      referenceSearch: '',
                      valueRange: { min: 0, max: 1000000 },
                    });
                  }}
                >
                  Clear Filters
                </Button>
              </div>
            )}

            <div className="flex items-center justify-between text-sm text-text-secondary">
              <p>
                Showing {sortedQuotes.length} of {quotes.length} quotes
              </p>
              <div className="flex items-center gap-4">
                <p>Keyboard shortcuts: j/k (navigate), d (duplicate), enter (open)</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default QuotesOverviewDashboard;